import {useState} from "react";
import {ListNotesPanelBody} from "~/components/notes/listNotesSidePanel";
import type {ListNotesPanelContext} from "~/components/notes/listNotesPanelTypes";
import {NOTES_BODY_MAX_PLAIN_LENGTH} from "~/domainModels/notes";
import {useUnsavedChangesGuard} from "~/hooks/useUnsavedChangesGuard";
import {RowEngagementModal} from "~/components/ottopilotDataViewer/RowEngagementModal";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeText} from "@vesta/ananke/components/anankeText";

type ListNotesComposerFooterState = {
    plainLength: number;
    canSave: boolean;
    isSaving: boolean;
    isUploading: boolean;
    onSave: () => void;
};

const INITIAL_COMPOSER_FOOTER_STATE: ListNotesComposerFooterState = {
    plainLength: 0,
    canSave: false,
    isSaving: false,
    isUploading: false,
    onSave: () => {},
};

export function ListNotesModal({
    context,
    onClose,
}: {
    context: ListNotesPanelContext;
    onClose: () => void;
}) {
    const [eventContainer, setEventContainer] = useState(context.eventContainer);
    const [composerFooterState, setComposerFooterState] = useState<ListNotesComposerFooterState>(
        INITIAL_COMPOSER_FOOTER_STATE,
    );
    const [isDirty, setIsDirty] = useState(false);
    const showHistory = context.showHistory === true;

    const {requestClose, modal} = useUnsavedChangesGuard({isDirty, onDiscard: onClose});

    const modalFooter =
        context.readOnly !== true ? (
            <div className="grid w-full grid-cols-[minmax(0,1fr)_max-content] items-center gap-ds-space-100">
                <AnankeText size="small" color="color.text.subtlest">
                    {composerFooterState.plainLength}/{NOTES_BODY_MAX_PLAIN_LENGTH}
                </AnankeText>
                <AnankeButton
                    type="button"
                    appearance="primary"
                    isDisabled={composerFooterState.canSave === false}
                    isLoading={composerFooterState.isSaving === true || composerFooterState.isUploading === true}
                    onClick={composerFooterState.onSave}
                >
                    Save
                </AnankeButton>
            </div>
        ) : undefined;

    return (
        <>
            <RowEngagementModal
                opened={true}
                title="Notes"
                onClose={() => requestClose()}
                footer={modalFooter}
                preventCloseWhileLoading={true}
                isLoading={composerFooterState.isSaving === true || composerFooterState.isUploading === true}
            >
                <ListNotesPanelBody
                    context={context}
                    eventContainer={eventContainer}
                    onEventContainerPatched={(nextEventContainer) => {
                        setEventContainer(nextEventContainer);
                        if (showHistory !== true) {
                            onClose();
                        }
                    }}
                    isFullscreen={false}
                    showHistory={showHistory}
                    showSortControl={showHistory}
                    composerFooterPlacement="external"
                    onNewNoteComposerStateChange={setComposerFooterState}
                    onDirtyChange={setIsDirty}
                />
            </RowEngagementModal>
            {modal}
        </>
    );
}
